import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild, signal } from '@angular/core';

import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
	AlertComponent,
	IMenu,
	IStepProgress,
	LinkComponent,
	ModalComponent,
	StepperMicroComponent,
	TileComponent,
} from '@tecalisdev/infinity';

import { MenuItemAction } from '../../../shared/models/master.models';

import { LanguageService } from '../../../core/services/language.service';

import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';

import { WorkflowsHelperService } from '../../services/workflows-helper.service';

import { WorkflowFormDefaultsService } from '../../services/workflow-form-defaults.service';

import { ITextLocaleMap } from '../../models/workflows.models';
import {
	hasWorkflowLocaleText,
	normalizeWorkflowLocaleText,
	serializeWorkflowLocaleFormValue,
	workflowLocaleMapsEqual,
	emptyWorkflowLocaleMap,
} from '../../utils/workflow-locale-text.util';
import {
	patchFormStepFromApi,
	patchFormStepToEditor,
	serializeFormStepToApi,
} from '../../utils/form-operation-contract.util';

import { FormStepAdvancedConfigComponent } from './field-config/form-step-advanced-config.component';
import { FormStepEditablePresetConfigComponent } from './field-config/form-step-editable-preset-config.component';
import { FormStepLockedPresetConfigComponent } from './field-config/form-step-locked-preset-config.component';
import { FormStepReadonlyViewComponent } from './field-config/form-step-readonly-view.component';
import { FormStepTypePickerComponent } from './field-config/form-step-type-picker.component';
import {
	findFormFieldPreset,
	FORM_FIELD_PRESETS,
	IFormFieldPreset,
	IFormFieldPresetFieldDef,
	inferFormFieldPresetId,
	isFormFieldMatchOcrTargetField,
	isFormFieldPresetMatchOcrEligible,
	resolveFormFieldMatchOcrDefaultForField,
} from './form-field-presets';
export type { WorkflowFormFieldType } from './form-field-types';
import { resolveDefaultTypeLabelKey, resolvePresetFieldPreviewTypeLabel } from './form-step-field-labels';
import {
	applyFormStepOptionsValidators,
	getFieldOptionsArray,
	normalizeFormStepFieldOptions,
	normalizeFormStepRichTextFieldOptions,
	patchRichTextOptionLabels,
	removeFormStepOption,
	sanitizeLockedPresetFieldsValidation,
	seedFormStepDefaultOptions,
	seedFormStepYesNoOptions,
} from './form-step-options.helper';
import {
	applyFormStepFieldTitleValidators,
	presetAppliesSerializedFieldTitleFallback,
} from './form-step-field-title.util';

@Component({
	selector: 'app-modal-workflow-form-step',

	imports: [
		ModalComponent,
		ReactiveFormsModule,
		TranslatePipe,
		AlertComponent,
		TileComponent,
		StepperMicroComponent,
		LinkComponent,
		FormStepReadonlyViewComponent,
		FormStepTypePickerComponent,
		FormStepLockedPresetConfigComponent,
		FormStepEditablePresetConfigComponent,
		FormStepAdvancedConfigComponent,
	],
	templateUrl: './modal-workflow-form-step.component.html',
	styleUrl: './modal-workflow-form-step.component.scss',
})
export class ModalWorkflowFormStepComponent implements OnInit {
	private trans = inject(TranslateService);
	private languageService = inject(LanguageService);
	private workflowFormDefaults = inject(WorkflowFormDefaultsService);
	public reactiveFormService = inject(ReactiveFormsService);
	public workflowsHelper = inject(WorkflowsHelperService);

	@Input() info: { mode: MenuItemAction; form?: Record<string, unknown>; index?: number };
	@Output() closeEvent = new EventEmitter<{ form: Record<string, unknown>; index?: number }>();

	/** Preset config visible (locked o editable); delega foco i18n a `form-step-instructions`. */
	@ViewChild(FormStepLockedPresetConfigComponent)
	private lockedPresetConfig?: FormStepLockedPresetConfigComponent;

	@ViewChild(FormStepEditablePresetConfigComponent)
	private editablePresetConfig?: FormStepEditablePresetConfigComponent;

	public MenuItemAction = MenuItemAction;
	public form: FormGroup = this.workflowsHelper.createFormStepGroup();
	public errorMsg = '';
	public fieldPresets = FORM_FIELD_PRESETS;
	public selectedPresetId: string | null = null;
	public addingField = false;
	private lastAppliedPresetId: string | null = null;

	/** Estado reactivo del switch «Cotejar datos» (sincroniza visibilidad del campo Descripción en la tile principal). */
	public readonly matchOcrEnabled = signal(false);

	public steps: IStepProgress[] = [
		{
			id: 'type',
			label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.WIZARD_TYPE'),
			selected: true,
		},
		{
			id: 'config',
			label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.WIZARD_CONFIG'),
			selected: false,
		},
	];

	public fieldMenuOpt: IMenu[] = [{ id: MenuItemAction.DELETE, label: this.trans.instant('BUTTON.DELETE') }];

	ngOnInit(): void {
		if (this.info?.form) {
			this.patchStepForm(this.info.form);
		}

		if (!this.isReadOnly && this.fieldsArray.length) {
			const fields = this.fieldsArray.controls.map((control) => control.getRawValue() as Record<string, unknown>);

			this.selectedPresetId = inferFormFieldPresetId(fields[0] ?? {}, fields);

			this.lastAppliedPresetId = this.selectedPresetId;
			this.patchRichTextLabelsForPreset();

			this.goToConfigStep();
		} else if (!this.isReadOnly) {
			this.goToTypeStep();
		}

		if (this.isReadOnly) {
			this.form.disable();
		}
	}

	get isReadOnly(): boolean {
		return this.info?.mode === MenuItemAction.SHOW;
	}

	get title(): string {
		if (this.info?.mode === MenuItemAction.SHOW) {
			return this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.VIEW_TITLE');
		}

		return this.info?.mode === MenuItemAction.EDIT
			? this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.EDIT_TITLE')
			: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.CREATE_TITLE');
	}

	get buttonPrimaryLabel(): string | undefined {
		if (this.isReadOnly) {
			return undefined;
		}

		return this.isConfigStepSelected ? this.trans.instant('BUTTON.SAVE') : this.trans.instant('BUTTON.CONTINUE');
	}

	get isConfigStepSelected(): boolean {
		return Boolean(this.steps.at(1)?.selected);
	}

	get selectedPreset(): IFormFieldPreset | undefined {
		return findFormFieldPreset(this.selectedPresetId);
	}

	get fieldsArray(): FormArray {
		return this.form.get('fields') as FormArray;
	}

	get primaryField(): FormGroup | null {
		return (this.fieldsArray.at(0) as FormGroup) ?? null;
	}

	get isMatchOcrEnabled(): boolean {
		return this.matchOcrEnabled();
	}

	get isFieldMetadataLocked(): boolean {
		return Boolean(this.selectedPreset?.fieldMetadataLocked);
	}

	get usesOptionsEditor(): boolean {
		return Boolean(this.selectedPreset?.usesOptionsEditor);
	}

	get usesRichTextOptionsEditor(): boolean {
		return Boolean(this.selectedPreset?.usesRichTextOptionsEditor);
	}

	get isRequiredLocked(): boolean {
		return false;
	}

	get isMatchOcrLocked(): boolean {
		return false;
	}

	get supportsMatchOcrConfig(): boolean {
		return isFormFieldPresetMatchOcrEligible(this.selectedPresetId);
	}

	public closeModal(action: string): void {
		if (this.isReadOnly) {
			if (action !== 'buttonPrimary') {
				this.closeEvent.emit(null);
			}

			return;
		}

		if (action !== 'buttonPrimary') {
			this.closeEvent.emit(null);

			return;
		}

		if (!this.isConfigStepSelected) {
			this.continueFromTypeStep();

			return;
		}

		this.saveStep();
	}

	public clickStep(step: IStepProgress): void {
		if (this.isReadOnly) {
			return;
		}

		if (step.id === 'config') {
			if (!this.selectedPresetId) {
				this.errorMsg = this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.TYPE_REQUIRED');

				return;
			}

			if (!this.fieldsArray.length || this.addingField) {
				this.applyPresetSelection();
			}

			this.goToConfigStep();

			return;
		}

		this.errorMsg = '';

		this.steps.forEach((item) => {
			item.selected = false;
		});

		step.selected = true;
	}

	public selectPreset(preset: IFormFieldPreset): void {
		this.selectedPresetId = preset.id;

		this.resetStepFormValidationState();
	}

	public editType(): void {
		this.addingField = false;

		this.resetStepFormValidationState();

		this.steps.forEach((item) => {
			item.selected = item.id === 'type';
		});
	}

	public addAnotherField(): void {
		this.addingField = true;

		this.selectedPresetId = null;

		this.resetStepFormValidationState();

		this.steps.forEach((item) => {
			item.selected = item.id === 'type';
		});
	}

	public fieldMenuChange(action: IMenu, index: number): void {
		if (action.id === MenuItemAction.DELETE) {
			this.removeField(index);
		}
	}

	public presetFieldPreviewTypeLabel(index: number): string {
		return resolvePresetFieldPreviewTypeLabel(
			this.trans,
			this.selectedPreset,
			String(this.fieldsArray.at(index)?.get('type')?.value ?? 'text'),
		);
	}

	public optionsArray(fieldIndex: number): FormArray {
		return getFieldOptionsArray(this.fieldsArray, fieldIndex);
	}

	public removeOption(fieldIndex: number, optionIndex: number): void {
		removeFormStepOption(this.fieldsArray, fieldIndex, optionIndex);
	}

	public removeField(index: number): void {
		this.fieldsArray.removeAt(index);

		if (!this.fieldsArray.length) {
			this.selectedPresetId = null;
		}
	}

	public isFieldRequired(): boolean {
		return this.fieldsArray.controls.some((control) => {
			const validators = (control.get('validators')?.value as string[]) ?? [];

			return validators.includes('required');
		});
	}

	public setFieldRequired(required: boolean): void {
		this.fieldsArray.controls.forEach((control) => {
			const validators = [...((control.get('validators')?.value as string[]) ?? [])];

			const next = required
				? [...new Set([...validators, 'required'])]
				: validators.filter((validator) => validator !== 'required');

			control.get('validators')?.setValue(next);
		});
	}

	public onMatchOcrChange(enabled: boolean): void {
		if (!this.supportsMatchOcrConfig) return;

		this.fieldsArray.controls.forEach((control) => {
			const fieldId = String((control as FormGroup).get('id')?.value ?? '');
			const isTarget = isFormFieldMatchOcrTargetField(this.selectedPresetId, fieldId);
			this.syncMatchOcrOnField(control as FormGroup, enabled && isTarget);
		});

		this.matchOcrEnabled.set(enabled);
	}

	private syncMatchOcrOnField(field: FormGroup, enabled: boolean): void {
		field.get('match_ocr')?.setValue(enabled);

		const validators = [...((field.get('validators')?.value as string[]) ?? [])];

		const next = enabled
			? [...new Set([...validators, 'match_ocr'])]
			: validators.filter((validator) => validator !== 'match_ocr');

		field.get('validators')?.setValue(next);
	}

	private refreshMatchOcrEnabledState(): void {
		this.matchOcrEnabled.set(this.fieldsArray.controls.some((control) => Boolean(control.get('match_ocr')?.value)));
	}

	private continueFromTypeStep(): void {
		this.errorMsg = '';

		if (!this.selectedPresetId) {
			this.errorMsg = this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.TYPE_REQUIRED');

			return;
		}

		this.applyPresetSelection();

		this.goToConfigStep();
	}

	private applyPresetSelection(): void {
		this.resetStepFormValidationState();

		const preset = findFormFieldPreset(this.selectedPresetId);

		if (!preset) {
			return;
		}

		if (this.addingField) {
			this.addingField = false;

			this.fieldsArray.push(this.buildFieldGroupFromPreset(preset));
		} else {
			this.fieldsArray.clear();

			if (preset.presetFields?.length) {
				preset.presetFields.forEach((fieldDef) => {
					this.fieldsArray.push(this.buildFieldGroupFromFieldDef(fieldDef));
				});
			} else {
				this.fieldsArray.push(this.buildFieldGroupFromPreset(preset));
			}
		}

		this.applyPresetStepDefaults(preset);

		this.workflowsHelper.applyFormStepGuidelineValidatorsForPreset(this.form.get('step_name') as FormGroup, preset);
		this.workflowsHelper.applyFormStepDescriptionValidators(this.form.get('step_description') as FormGroup);

		this.syncPresetFieldControls(preset);

		applyFormStepOptionsValidators(this.fieldsArray, this.workflowsHelper, preset);
		applyFormStepFieldTitleValidators(this.fieldsArray, this.workflowsHelper, preset);

		this.refreshMatchOcrEnabledState();

		if (preset.fieldMetadataLocked) {
			sanitizeLockedPresetFieldsValidation(this.fieldsArray);
		}
	}

	private resolvePresetFieldDefs(preset: IFormFieldPreset): IFormFieldPresetFieldDef[] {
		if (preset.presetFields?.length) {
			return preset.presetFields;
		}

		return [
			{
				titleKey: preset.fieldTitleKey,
				fieldName: preset.fieldName,
				fieldId: preset.fieldId,
				type: preset.type,
				typeLabelKey: resolveDefaultTypeLabelKey(preset.type),
				required: preset.required,
			},
		];
	}

	get matchOcrTargetField(): FormGroup | null {
		if (!isFormFieldPresetMatchOcrEligible(this.selectedPresetId)) {
			return null;
		}

		const target = this.fieldsArray.controls.find((control) =>
			isFormFieldMatchOcrTargetField(this.selectedPresetId, String(control.get('id')?.value ?? '')),
		);

		return (target as FormGroup | undefined) ?? this.primaryField;
	}

	private syncPresetFieldControls(preset: IFormFieldPreset): void {
		const metadataKeys = ['id', 'name', 'type', 'title', 'col'] as const;

		this.fieldsArray.controls.forEach((control) => {
			if (preset.fieldMetadataLocked) {
				metadataKeys.forEach((key) => control.get(key)?.disable({ emitEvent: false }));
			} else {
				metadataKeys.forEach((key) => control.get(key)?.enable({ emitEvent: false }));
			}
		});
	}

	private applyPresetStepDefaults(preset: IFormFieldPreset): void {
		const allowed = this.languageService.getAllowedLanguages();
		const stepNameGroup = this.form.get('step_name') as FormGroup;
		const stepDescriptionGroup = this.form.get('step_description') as FormGroup;

		const currentPauta = serializeWorkflowLocaleFormValue(stepNameGroup?.getRawValue(), allowed);
		const hasAnyPauta = hasWorkflowLocaleText(currentPauta);

		const previousPreset = findFormFieldPreset(this.lastAppliedPresetId);
		const previousDefaultPauta = previousPreset
			? this.resolveFormStepLocaleDefaults(previousPreset.defaultQuestionKey)
			: emptyWorkflowLocaleMap(allowed);

		const newDefaultPauta = this.resolveFormStepLocaleDefaults(preset.defaultQuestionKey);

		if (preset.fieldMetadataLocked || preset.prefillGuideline) {
			if (!hasAnyPauta || workflowLocaleMapsEqual(currentPauta, previousDefaultPauta, allowed)) {
				stepNameGroup.patchValue(newDefaultPauta);
			}
		} else if (!hasAnyPauta || workflowLocaleMapsEqual(currentPauta, previousDefaultPauta, allowed)) {
			stepNameGroup.patchValue(emptyWorkflowLocaleMap(allowed));
		}

		const defaultDescription = this.resolveFormStepLocaleDefaults(
			'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.MATCH_OCR_DEFAULT_ALERT',
		);
		const currentDescription = serializeWorkflowLocaleFormValue(stepDescriptionGroup?.getRawValue(), allowed);

		if (preset.prefillStepDescription) {
			if (
				!hasWorkflowLocaleText(currentDescription) ||
				workflowLocaleMapsEqual(currentDescription, defaultDescription, allowed)
			) {
				stepDescriptionGroup.patchValue(defaultDescription);
			}
		} else if (
			!hasWorkflowLocaleText(currentDescription) ||
			workflowLocaleMapsEqual(currentDescription, defaultDescription, allowed) ||
			previousPreset?.prefillStepDescription
		) {
			stepDescriptionGroup.patchValue(emptyWorkflowLocaleMap(allowed));
		}

		this.lastAppliedPresetId = preset.id;
	}

	/** Defaults multi-idioma; en tests sin preload de bundles usa ngx-translate en todos los locales. */
	private resolveFormStepLocaleDefaults(i18nKey: string): ITextLocaleMap {
		try {
			return this.workflowFormDefaults.getLocaleMapForKey(i18nKey);
		} catch {
			const allowed = this.languageService.getAllowedLanguages();
			const text = this.trans.instant(i18nKey);
			return Object.fromEntries(allowed.map((l) => [l, text])) as ITextLocaleMap;
		}
	}

	private buildFieldGroupFromFieldDef(fieldDef: IFormFieldPresetFieldDef): FormGroup {
		const fieldGroup = this.workflowsHelper.createFormFieldGroup();

		const validators: string[] = [];

		if (fieldDef.required) {
			validators.push('required');
		}

		const matchOcrDefault = resolveFormFieldMatchOcrDefaultForField(this.selectedPresetId, fieldDef.fieldId);

		fieldGroup.patchValue({
			id: fieldDef.fieldId,
			name: fieldDef.fieldName,
			type: fieldDef.type,
			title: this.resolveFormStepLocaleDefaults(fieldDef.titleKey),
			col: 12,
			match_ocr: matchOcrDefault,
			validators: matchOcrDefault ? [...validators, 'match_ocr'] : validators,
		});

		return fieldGroup;
	}

	private buildFieldGroupFromPreset(preset: IFormFieldPreset): FormGroup {
		const fieldGroup = this.buildFieldGroupFromFieldDef(this.resolvePresetFieldDefs(preset)[0]);

		if (!preset.fieldMetadataLocked) {
			const titleGroup = fieldGroup.get('title') as FormGroup;
			const allowed = this.languageService.getAllowedLanguages();

			titleGroup?.patchValue(emptyWorkflowLocaleMap(allowed));
			titleGroup?.markAsUntouched();
			titleGroup?.updateValueAndValidity();
		}

		if (preset.usesLegalFieldsConfig) {
			const options = fieldGroup.get('options') as FormArray;

			options.clear();
			seedFormStepDefaultOptions(options, this.workflowsHelper, preset.defaultOptionsCount ?? 1, true);
		} else if (preset.usesYesNoPreview) {
			const options = fieldGroup.get('options') as FormArray;

			seedFormStepYesNoOptions(
				options,
				this.workflowsHelper,
				this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_YES_NO_OPTION_YES'),
				this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_YES_NO_OPTION_NO'),
			);
		} else if (preset.usesOptionsEditor) {
			const options = fieldGroup.get('options') as FormArray;

			options.clear();
			seedFormStepDefaultOptions(
				options,
				this.workflowsHelper,
				preset.defaultOptionsCount ?? 2,
				Boolean(preset.usesRichTextOptionsEditor),
			);
		}

		return fieldGroup;
	}

	private goToTypeStep(): void {
		this.resetStepFormValidationState();
		this.steps.forEach((item) => {
			item.selected = item.id === 'type';
		});
	}

	private goToConfigStep(): void {
		this.errorMsg = '';

		this.steps.forEach((item) => {
			item.selected = item.id === 'config';
		});
	}

	/** Salta a la pestaña del primer idioma inválido (vía preset config → form-step-instructions). */
	private focusFormStepLocaleValidationErrors(): void {
		this.lockedPresetConfig?.focusLocaleValidationErrors();
		this.editablePresetConfig?.focusLocaleValidationErrors();
	}

	private saveStep(): void {
		this.errorMsg = '';

		this.form.markAllAsTouched();

		if (this.form.invalid) {
			this.focusFormStepLocaleValidationErrors();
			this.errorMsg = this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.VALIDATION_ERROR');

			return;
		}

		if (!this.fieldsArray.length) {
			this.errorMsg = this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELDS_REQUIRED');

			return;
		}

		this.closeEvent.emit({
			form: this.serializeStep(),
			index: this.info?.index,
		});
	}

	private patchStepForm(raw: Record<string, unknown>): void {
		const allowed = this.languageService.getAllowedLanguages();
		const patchedStep = patchFormStepToEditor(raw, allowed);
		const preset = this.selectedPresetId ? findFormFieldPreset(this.selectedPresetId) : null;
		const richTextOptions = Boolean(preset?.usesRichTextOptionsEditor || preset?.usesLegalFieldsConfig);

		this.form.patchValue({
			uuid: patchedStep['uuid'],
			step_id: patchedStep['step_id'],
			step_name: patchedStep['step_name'],
			step_description: patchedStep['step_description'],
			alert_msg: patchedStep['alert_msg'],
		});

		const fields = (patchedStep['fields'] as Record<string, unknown>[]) ?? [];

		fields.forEach((field) => {
			const fieldGroup = this.workflowsHelper.createFormFieldGroup();

			fieldGroup.patchValue(this.workflowsHelper.patchFormFieldValue(field));

			((field['options'] as { value: string; label: Record<string, string> }[]) ?? []).forEach((opt) => {
				const optGroup = this.workflowsHelper.createFormFieldOptionGroup(richTextOptions);

				optGroup.patchValue(opt);

				(fieldGroup.get('options') as FormArray).push(optGroup);
			});

			this.fieldsArray.push(fieldGroup);
		});

		if (preset) {
			this.workflowsHelper.applyFormStepGuidelineValidatorsForPreset(
				this.form.get('step_name') as FormGroup,
				preset,
			);
			this.workflowsHelper.applyFormStepDescriptionValidators(this.form.get('step_description') as FormGroup);

			this.syncPresetFieldControls(preset);
			applyFormStepOptionsValidators(this.fieldsArray, this.workflowsHelper, preset);
			applyFormStepFieldTitleValidators(this.fieldsArray, this.workflowsHelper, preset);

			if (preset.fieldMetadataLocked) {
				sanitizeLockedPresetFieldsValidation(this.fieldsArray);
			}

			if (richTextOptions && this.primaryField) {
				patchRichTextOptionLabels(this.primaryField.get('options') as FormArray);
			}
		}

		this.refreshMatchOcrEnabledState();
	}

	private patchRichTextLabelsForPreset(): void {
		const preset = findFormFieldPreset(this.selectedPresetId);

		if ((preset?.usesRichTextOptionsEditor || preset?.usesLegalFieldsConfig) && this.primaryField) {
			patchRichTextOptionLabels(this.primaryField.get('options') as FormArray);
		}
	}

	private serializeStep(): Record<string, unknown> {
		const raw = this.form.getRawValue();
		const allowed = this.languageService.getAllowedLanguages();

		const fields = (raw.fields as Record<string, unknown>[]).map((field) => {
			const fieldRecord = { ...field };
			const allowedLocales = this.languageService.getAllowedLanguages();
			const titleMap = serializeWorkflowLocaleFormValue(fieldRecord['title'], allowedLocales);

			if (
				!hasWorkflowLocaleText(titleMap) &&
				this.selectedPreset &&
				presetAppliesSerializedFieldTitleFallback(this.selectedPreset)
			) {
				fieldRecord['title'] = this.resolveFormStepLocaleDefaults(this.selectedPreset.fieldTitleKey);
			}

			let normalizedField = fieldRecord;

			if (this.selectedPreset?.usesRichTextOptionsEditor || this.selectedPreset?.usesLegalFieldsConfig) {
				normalizedField = normalizeFormStepRichTextFieldOptions(fieldRecord, allowedLocales);
			} else if (this.selectedPreset?.usesOptionsEditor) {
				normalizedField = normalizeFormStepFieldOptions(fieldRecord, allowedLocales);
			}

			return this.workflowsHelper.serializeFormField(normalizedField);
		});

		const stepDescriptionMap = serializeWorkflowLocaleFormValue(raw.step_description, allowed);
		const hasDescription = hasWorkflowLocaleText(stepDescriptionMap);
		const stepNameMap = serializeWorkflowLocaleFormValue(raw.step_name, allowed);

		return serializeFormStepToApi(
			{
				uuid: raw.uuid,
				step_id: Number(raw.step_id),
				step_name: stepNameMap,
				step_description: hasDescription ? stepDescriptionMap : undefined,
				alert_msg: this.isMatchOcrEnabled && hasDescription ? stepDescriptionMap : undefined,
				fields,
			},
			allowed,
			(field) => field,
		);
	}

	private resetStepFormValidationState(): void {
		this.resetControlValidationState(this.form);
		this.errorMsg = '';
	}

	private resetControlValidationState(control: AbstractControl): void {
		if (control instanceof FormGroup) {
			Object.values(control.controls).forEach((child) => this.resetControlValidationState(child));
		} else if (control instanceof FormArray) {
			control.controls.forEach((child) => this.resetControlValidationState(child));
		}

		control.markAsPristine();
		control.markAsUntouched();
		control.updateValueAndValidity({ emitEvent: false });
	}
}
